/**
 * The "add another X" card — a dimmed, dashed "+" placeholder that sits
 * INSIDE a grid of X in the same footprint as the real cards (subjects on
 * triage, scopes on a partner). Adding one reads as "one more of these",
 * never as a toolbar action; every grid that grows by hand uses this.
 */

import type { ReactNode } from 'react';

import { Card, Stack, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

import classes from './AddCard.module.css';

export interface AddCardProps {
  /** The card's headline, e.g. "Add subject". */
  label: string;
  /** One line under the headline saying what gets added. */
  hint?: ReactNode;
  onClick: () => void;
  /** Accessible name; defaults to the label. */
  ariaLabel?: string;
  'data-testid'?: string;
}

export function AddCard({
  label,
  hint,
  onClick,
  ariaLabel,
  'data-testid': testId,
}: Readonly<AddCardProps>) {
  return (
    <Card
      component="button"
      type="button"
      withBorder
      radius="md"
      padding="md"
      className={classes.addCard}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      data-testid={testId}
    >
      <Stack gap={4} align="center">
        <IconPlus size={36} stroke={1.5} />
        <Text size="sm" fw={500}>
          {label}
        </Text>
        {hint && (
          <Text size="xs" c="dimmed" ta="center">
            {hint}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
